if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        // NOTE: both the `.product__media-toggle` zoom button AND the actual
        // media wrapper (`.product-media-autoplay`, `product-model`, etc.)
        // carry data-media-id in product-thumbnail.liquid. The button comes
        // first in document order, so a plain `[data-media-id="..."]` query
        // would resolve to the button — which doesn't contain the <video> —
        // and silently break autoplay-resume. Explicitly skip button matches.
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not(button)`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(button)');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        // Original Dawn behavior: click-to-load videos/3D models still work
        // as before. activeItem can itself be the .deferred-media element
        // (not just contain one), so check both cases.
        const deferredMedia = activeItem.classList.contains('deferred-media')
          ? activeItem
          : activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);
          return;
        }

        // New behavior: autoplaying videos need to be explicitly resumed
        // here, since pauseAllMedia() just paused them and nothing else in
        // the click flow will call .play() again. Search activeItem's own
        // subtree directly for the video/iframe rather than relying on a
        // wrapper class, since activeItem itself may already be that wrapper.
        const video = activeItem.querySelector('video');
        if (video) {
          video.muted = true;
          const playPromise = video.play();
          if (playPromise !== undefined) playPromise.catch(() => {});
        } else {
          // External videos (YouTube/Vimeo) rendered as an iframe can't be
          // resumed via video.play(), so reload the iframe src to restart it.
          const iframe = activeItem.querySelector('iframe');
          if (iframe) {
            // eslint-disable-next-line no-self-assign
            iframe.src = iframe.src;
          }
        }

        // pauseAllMedia() pauses every video/iframe on the page, not just
        // the one belonging to activeItem. Since product-thumbnail.liquid
        // renders video/external_video media so it autoplays immediately
        // (rather than requiring a click), there can be other autoplaying
        // media elsewhere in the gallery (e.g. in a stacked/columns layout)
        // that just got paused as a side effect of switching the active
        // slide. Those should keep autoplaying regardless of which
        // thumbnail is currently "active", so resume them via the shared
        // helper defined in global.js (also used after modals close).
        if (window.resumeGalleryAutoplayMedia) window.resumeGalleryAutoplayMedia();
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}